import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";
import { IMAGES } from "@/data/content";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ onOpenSearch }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[85vh] flex items-end overflow-hidden">
      <motion.img
        src={IMAGES.hero}
        alt="Kiki Services — consulting, business and digital"
        style={{ y: imgY }}
        className="absolute inset-0 w-full h-[115%] object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#FAF5EC] via-[#FAF5EC]/60 to-[#FAF5EC]/25" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pb-20 pt-44">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="text-sm uppercase tracking-[0.35em] font-mono text-[#7A2E2A] font-bold mb-6"
        >
          Consulting · Business · Digital
        </motion.p>

        <h1 className="font-display font-medium tracking-tight leading-[1.04] text-4xl sm:text-6xl lg:text-7xl text-kiki-ink max-w-4xl">
          <MaskedLine delay={0.5}>Empowering business</MaskedLine>
          <MaskedLine delay={0.65} className="italic text-kiki-gold">
            growth strategies
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-lg text-kiki-ink font-medium leading-relaxed"
        >
          Practical HR, payroll, process, data, brand and digital support for
          startups, SMEs, founders and growing businesses.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="find-service-btn"
            onClick={onOpenSearch}
            className="group flex items-center gap-2.5 bg-white/95 hover:bg-white text-kiki-gold font-outfit font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-kiki-ink/10 border border-kiki-line transition-colors duration-300"
          >
            <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
            Find my service
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
